/**
 * OWA-1149: how large a document's file may be. A file whose declared content type a server reads
 * text from (the Office types, PDF and plain text) may be at most 100 MB; any other file at most
 * 200 MB, the most the servers accept today. The list and the limits are `uploadLimits.json`, a
 * byte-for-byte copy of the Rust server's `src/practice/server/src/documents/upload_limits.json`
 * (the Rust repo's `tests/upload_limits_parity_tests.rs` compares them), so a file the server
 * would refuse is refused here before anything is queued or uploaded.
 *
 * The class is decided by the document's `contentType`, the field the server reads.
 *
 * @module client/Documents/uploadLimits
 */
import limits from './uploadLimits.json';
import Alert from '../lib/alert';

const MIB = 1024 * 1024;

export const SCANNED_MAX_BYTES: number = limits.scannedMaxBytes;
export const UNSCANNED_MAX_BYTES: number = limits.unscannedMaxBytes;

/**
 * A content type without its parameters, surrounding space or case, as the server compares it:
 * `Application/PDF; charset=binary` is `application/pdf`.
 */
export function normalizeContentType(contentType: string): string {
    return contentType.split(';')[0].trim().toLowerCase();
}

const SCANNED_TYPES = new Set(limits.scannedTypes.map(normalizeContentType));

/** Whether a server reads text from a file of this declared content type. */
export function isScannedType(contentType: unknown): boolean {
    return typeof contentType === 'string' && SCANNED_TYPES.has(normalizeContentType(contentType));
}

/** The most a file of this declared content type may hold, in bytes. */
export function maxUploadBytes(contentType: unknown): number {
    return isScannedType(contentType) ? SCANNED_MAX_BYTES : UNSCANNED_MAX_BYTES;
}

/** `100 MB` for 100 MiB; a size is rounded up, so a file just past a limit never reads as at it. */
function megabytes(bytes: number): string {
    return `${Math.ceil(bytes / MIB)} MB`;
}

/**
 * Why this file cannot be uploaded, worded for the person uploading it, or null when it can.
 *
 * @param {string} fileName - the name shown to the person
 * @param {number} size - the file's size in bytes
 * @param {unknown} contentType - the content type the document will declare
 */
export function uploadSizeRefusal(fileName: string, size: number, contentType: unknown): string | null {
    if (!(size > maxUploadBytes(contentType))) return null;
    if (isScannedType(contentType)) {
        return `${fileName} is ${megabytes(size)}. PDF, Word, Excel, PowerPoint and plain text files can be at most `
            + `${megabytes(SCANNED_MAX_BYTES)}, because Outlaw reads their text. Split the file or save a smaller copy.`;
    }
    return `${fileName} is ${megabytes(size)}. Files can be at most ${megabytes(UNSCANNED_MAX_BYTES)}.`;
}

/** How long a refusal stays on screen: it is longer than most alerts. */
export const REFUSAL_DURATION_MS = 15_000;

/**
 * Shows a size refusal as a warning. A refusal is the person's to act on, not a fault to report,
 * and its message names the file, which can name a client: it goes through `Alert.addMany`, which
 * puts it in the store for the alert display without the exception and log telemetry that
 * `Alert.add` (errors) and the alert reducer's single-add path (warnings and errors) send.
 */
export function showUploadRefusal(message: string): void {
    Alert.addMany([new Alert(Alert.WARNING, null, null, message, REFUSAL_DURATION_MS)]);
}

/** Shows the refusal for this file when it is too large, and answers whether it was refused. */
export function refuseOversizedFile(fileName: string, size: number, contentType: unknown): boolean {
    const refusal = uploadSizeRefusal(fileName, size, contentType);
    if (!refusal) return false;
    showUploadRefusal(refusal);
    return true;
}
